import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, Upload, Image, Type, Mail, Phone, FileText,
  Eye, Save, RotateCcw, Check, Loader2, X, Sun, Moon,
  Monitor, Smartphone, FileCheck, Paintbrush, ExternalLink,
  Building2, Lock, EyeOff, FileDown, Pill, Stethoscope,
  TestTube, Calendar, User, MapPin, ClipboardList, Sparkles,
  Heart, Leaf, Waves, Sunset, Mountain, Zap, Shield, Plus,
  Trash2, Copy, Send, Bell, CreditCard, FileKey, Edit3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';

// Icon map for custom templates
const ICON_MAP = {
  Palette, Shield, Heart, Leaf, Waves, Sunset, Mountain, Zap, Sparkles, FileCheck, Building2
};

// Pre-designed branding templates
const BRANDING_TEMPLATES = [
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    description: 'Classic healthcare with a premium feel',
    icon: Shield,
    colors: {
      primary_color: '#4A238F',
      secondary_color: '#7C3AED',
      accent_color: '#F59E0B',
      text_color: '#1E293B',
      sidebar_color: '#4A238F',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #4A238F 0%, #7C3AED 100%)'
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    description: 'Calm and trustworthy medical aesthetic',
    icon: Waves,
    colors: {
      primary_color: '#0284C7',
      secondary_color: '#0EA5E9',
      accent_color: '#14B8A6',
      text_color: '#1E293B',
      sidebar_color: '#0C4A6E',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #0284C7 0%, #0EA5E9 100%)'
  },
  {
    id: 'emerald-health',
    name: 'Emerald Health',
    description: 'Fresh and natural wellness vibe',
    icon: Leaf,
    colors: {
      primary_color: '#059669',
      secondary_color: '#10B981',
      accent_color: '#F59E0B',
      text_color: '#1E293B',
      sidebar_color: '#064E3B',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #059669 0%, #10B981 100%)'
  },
  {
    id: 'warm-coral',
    name: 'Warm Coral',
    description: 'Friendly and approachable care',
    icon: Heart,
    colors: {
      primary_color: '#DC2626',
      secondary_color: '#F87171',
      accent_color: '#FBBF24',
      text_color: '#1E293B',
      sidebar_color: '#7F1D1D',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #DC2626 0%, #F87171 100%)'
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    description: 'Energetic and modern clinic',
    icon: Sunset,
    colors: {
      primary_color: '#EA580C',
      secondary_color: '#F97316',
      accent_color: '#FACC15',
      text_color: '#1E293B',
      sidebar_color: '#9A3412',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #EA580C 0%, #F97316 100%)'
  },
  {
    id: 'midnight-navy',
    name: 'Midnight Navy',
    description: 'Professional and sophisticated',
    icon: Moon,
    colors: {
      primary_color: '#1E3A8A',
      secondary_color: '#3B82F6',
      accent_color: '#F59E0B',
      text_color: '#1E293B',
      sidebar_color: '#1E3A8A',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)'
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    description: 'Traditional and reliable',
    icon: Mountain,
    colors: {
      primary_color: '#166534',
      secondary_color: '#22C55E',
      accent_color: '#A3E635',
      text_color: '#1E293B',
      sidebar_color: '#14532D',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #166534 0%, #22C55E 100%)'
  },
  {
    id: 'electric-teal',
    name: 'Electric Teal',
    description: 'Modern tech-forward hospital',
    icon: Zap,
    colors: {
      primary_color: '#0D9488',
      secondary_color: '#14B8A6',
      accent_color: '#8B5CF6',
      text_color: '#1E293B',
      sidebar_color: '#134E4A',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)'
  },
  {
    id: 'rose-gold',
    name: 'Rose Gold',
    description: 'Elegant women\'s health clinic',
    icon: Sparkles,
    colors: {
      primary_color: '#BE185D',
      secondary_color: '#EC4899',
      accent_color: '#F59E0B',
      text_color: '#1E293B',
      sidebar_color: '#831843',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #BE185D 0%, #EC4899 100%)'
  },
  {
    id: 'slate-minimal',
    name: 'Slate Minimal',
    description: 'Clean and minimalist design',
    icon: FileCheck,
    colors: {
      primary_color: '#475569',
      secondary_color: '#64748B',
      accent_color: '#3B82F6',
      text_color: '#1E293B',
      sidebar_color: '#1E293B',
      sidebar_text_color: '#FFFFFF'
    },
    preview: 'linear-gradient(135deg, #475569 0%, #64748B 100%)'
  }
];

export default function BrandingSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    // Logo & Images
    logo_url: '',
    logo_dark_url: '',
    favicon_url: '',
    banner_url: '',
    login_background_url: '',
    // Colors
    primary_color: '#4A238F',
    secondary_color: '#7C3AED',
    accent_color: '#F59E0B',
    text_color: '#1E293B',
    background_color: '#FFFFFF',
    sidebar_color: '#1E1B4B',
    sidebar_text_color: '#FFFFFF',
    // Text
    tagline: '',
    footer_text: '',
    support_email: '',
    support_phone: '',
    // Login
    login_title: '',
    login_subtitle: '',
    show_powered_by: true,
    // Documents
    letterhead_enabled: true,
    letterhead_address: '',
    watermark_enabled: false,
    watermark_text: '',
    // Email
    email_header_color: '',
    email_footer_text: '',
    // Hospital info
    hospital_name: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [activeTab, setActiveTab] = useState('appearance');
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewType, setPdfPreviewType] = useState('prescription');
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Custom templates state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateIcon, setNewTemplateIcon] = useState('Palette');
  
  // Email templates state
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [emailBranding, setEmailBranding] = useState({});
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('appointment_confirmation');
  const [editingEmailTemplate, setEditingEmailTemplate] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');

  // File upload refs
  const logoInputRef = useRef(null);
  const logoDarkInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const loginBgInputRef = useRef(null);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    fetchBranding();
    fetchCustomTemplates();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const response = await api.get('/branding');
      setBranding(response.data);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const response = await api.get('/branding/templates');
      setCustomTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch custom templates:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/branding/email-templates');
      setEmailTemplates(response.data.templates || {});
      setEmailBranding(response.data.branding || {});
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
      toast.error('Failed to load email templates');
    }
  };

  const handleChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/branding', branding);
      toast.success('Branding settings saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save branding:', error);
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset all branding to defaults?')) return;
    
    try {
      const response = await api.post('/branding/reset');
      setBranding(prev => ({ ...prev, ...response.data.branding }));
      toast.success('Branding reset to defaults');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to reset branding:', error);
      toast.error('Failed to reset branding');
    }
  };

  const handleFileUpload = async (assetType, file, inputRef) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(prev => ({ ...prev, [assetType]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/branding/upload/${assetType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fieldMap = {
        logo: 'logo_url',
        logo_dark: 'logo_dark_url',
        favicon: 'favicon_url',
        banner: 'banner_url',
        login_background: 'login_background_url'
      };
      
      setBranding(prev => ({ ...prev, [fieldMap[assetType]]: response.data.url }));
      toast.success(`${assetType.replace('_', ' ')} uploaded successfully`);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [assetType]: false }));
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Apply a template to current branding
  const applyTemplate = (template) => {
    setBranding(prev => ({
      ...prev,
      ...template.colors
    }));
    setSelectedTemplate(template.id);
    setHasChanges(true);
    toast.success(`Applied "${template.name}" template. Don't forget to save!`);
    setShowTemplateGallery(false);
  };

  // Save current branding as custom template
  const saveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    try {
      const response = await api.post('/branding/templates', {
        name: newTemplateName,
        description: newTemplateDescription,
        icon: newTemplateIcon
      });
      
      setCustomTemplates(prev => [response.data.template, ...prev]);
      toast.success('Template saved successfully!');
      setShowSaveTemplateModal(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
      setNewTemplateIcon('Palette');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    }
  };

  // Delete a custom template
  const deleteCustomTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/branding/templates/${templateId}`);
      setCustomTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Email template functions
  const openEmailTemplates = async () => {
    setShowEmailTemplates(true);
    await fetchEmailTemplates();
  };

  const updateEmailTemplate = async () => {
    if (!editingEmailTemplate) return;
    
    try {
      await api.put(`/branding/email-templates/${selectedEmailTemplate}`, {
        subject: editingEmailTemplate.subject,
        body: editingEmailTemplate.body
      });
      
      setEmailTemplates(prev => ({
        ...prev,
        [selectedEmailTemplate]: {
          ...prev[selectedEmailTemplate],
          ...editingEmailTemplate
        }
      }));
      
      toast.success('Email template saved!');
      setEditingEmailTemplate(null);
    } catch (error) {
      console.error('Failed to update email template:', error);
      toast.error('Failed to save email template');
    }
  };

  const resetEmailTemplate = async (templateKey) => {
    if (!confirm('Reset this template to default?')) return;
    
    try {
      const response = await api.post(`/branding/email-templates/${templateKey}/reset`);
      setEmailTemplates(prev => ({
        ...prev,
        [templateKey]: response.data.template
      }));
      setEditingEmailTemplate(null);
      toast.success('Template reset to default');
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error('Failed to reset template');
    }
  };

  const previewEmailTemplate = async (template) => {
    setLoadingEmailPreview(true);
    try {
      const response = await api.post('/branding/email-templates/preview', {
        subject: template.subject,
        body: template.body
      });
      setEmailPreview(response.data);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setLoadingEmailPreview(false);
    }
  };

  const sendTestEmail = async (template, recipientEmail) => {
    if (!recipientEmail) {
      toast.error('Please enter a recipient email address');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      const response = await api.post('/branding/email-templates/send-test', {
        subject: template.subject,
        body: template.body,
        recipient_email: recipientEmail
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
        // Show preview HTML in console if email failed
        if (response.data.preview_html) {
          console.log('Email HTML Preview:', response.data.preview_html);
        }
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const removeImage = (field) => {
    setBranding(prev => ({ ...prev, [field]: '' }));
    setHasChanges(true);
  };

  const ImageUploader = ({ label, field, assetType, inputRef, description }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileUpload(assetType, e.target.files?.[0], inputRef)}
        className="hidden"
      />
      {branding[field] ? (
        <div className="relative inline-block">
          <img
            src={branding[field]}
            alt={label}
            className="max-h-24 max-w-full rounded-lg border border-slate-200 bg-white p-2"
          />
          <button
            type="button"
            onClick={() => removeImage(field)}
            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading[assetType]}
          className="w-full h-24 border-dashed flex flex-col gap-1"
        >
          {uploading[assetType] ? (
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          ) : (
            <>
              <Upload className="h-6 w-6 text-slate-400" />
              <span className="text-xs text-slate-500">Click to upload</span>
            </>
          )}
        </Button>
      )}
      {description && <p className="text-xs text-slate-500">{description}</p>}
    </div>
  );

  const ColorPicker = ({ label, field, description }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2">
        <input
          type="color"
          value={branding[field] || '#000000'}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-12 h-10 rounded cursor-pointer border border-slate-300"
        />
        <Input
          value={branding[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder="#000000"
          className="flex-1"
        />
      </div>
      {description && <p className="text-xs text-slate-500">{description}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#4A238F]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Palette className="h-7 w-7 text-[#4A238F]" />
              Branding Settings
            </h1>
            <p className="text-slate-600 mt-1">
              Customize your hospital's look and feel for a white-label experience
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-[#4A238F] hover:bg-[#3A1B72] text-white flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {hasChanges && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-800">
            <span className="text-sm">You have unsaved changes</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start border-b rounded-none bg-slate-100 p-0 h-auto">
                    <TabsTrigger 
                      value="appearance" 
                      className="rounded-none px-4 py-3 text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:border-b-2 data-[state=active]:border-[#4A238F] data-[state=active]:shadow-none"
                    >
                      <Paintbrush className="h-4 w-4 mr-2" />
                      Appearance
                    </TabsTrigger>
                    <TabsTrigger 
                      value="colors" 
                      className="rounded-none px-4 py-3 text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:border-b-2 data-[state=active]:border-[#4A238F] data-[state=active]:shadow-none"
                    >
                      <Palette className="h-4 w-4 mr-2" />
                      Colors
                    </TabsTrigger>
                    <TabsTrigger 
                      value="login" 
                      className="rounded-none px-4 py-3 text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:border-b-2 data-[state=active]:border-[#4A238F] data-[state=active]:shadow-none"
                    >
                      <Monitor className="h-4 w-4 mr-2" />
                      Login Page
                    </TabsTrigger>
                    <TabsTrigger 
                      value="documents" 
                      className="rounded-none px-4 py-3 text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:border-b-2 data-[state=active]:border-[#4A238F] data-[state=active]:shadow-none"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Documents
                    </TabsTrigger>
                    <TabsTrigger 
                      value="contact" 
                      className="rounded-none px-4 py-3 text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:border-b-2 data-[state=active]:border-[#4A238F] data-[state=active]:shadow-none"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Contact
                    </TabsTrigger>
                  </TabsList>

                  {/* Appearance Tab */}
                  <TabsContent value="appearance" className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <ImageUploader
                        label="Primary Logo"
                        field="logo_url"
                        assetType="logo"
                        inputRef={logoInputRef}
                        description="Used on light backgrounds. Recommended: 200x60px PNG"
                      />
                      <ImageUploader
                        label="Logo (Dark Mode)"
                        field="logo_dark_url"
                        assetType="logo_dark"
                        inputRef={logoDarkInputRef}
                        description="Used on dark backgrounds like sidebar"
                      />
                      <ImageUploader
                        label="Favicon"
                        field="favicon_url"
                        assetType="favicon"
                        inputRef={faviconInputRef}
                        description="Browser tab icon. Recommended: 32x32px PNG"
                      />
                      <ImageUploader
                        label="Banner Image"
                        field="banner_url"
                        assetType="banner"
                        inputRef={bannerInputRef}
                        description="Used in emails and marketing"
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Tagline</Label>
                        <Input
                          value={branding.tagline || ''}
                          onChange={(e) => handleChange('tagline', e.target.value)}
                          placeholder="Your health, our priority"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Footer Text</Label>
                        <Textarea
                          value={branding.footer_text || ''}
                          onChange={(e) => handleChange('footer_text', e.target.value)}
                          placeholder="© 2024 Your Hospital. All rights reserved."
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Colors Tab */}
                  <TabsContent value="colors" className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <ColorPicker
                        label="Primary Color"
                        field="primary_color"
                        description="Main brand color for buttons and highlights"
                      />
                      <ColorPicker
                        label="Secondary Color"
                        field="secondary_color"
                        description="Accent color for secondary elements"
                      />
                      <ColorPicker
                        label="Accent Color"
                        field="accent_color"
                        description="Used for notifications and alerts"
                      />
                      <ColorPicker
                        label="Text Color"
                        field="text_color"
                        description="Primary text color"
                      />
                      <ColorPicker
                        label="Sidebar Color"
                        field="sidebar_color"
                        description="Navigation sidebar background"
                      />
                      <ColorPicker
                        label="Sidebar Text"
                        field="sidebar_text_color"
                        description="Navigation text color"
                      />
                    </div>
                  </TabsContent>

                  {/* Login Page Tab */}
                  <TabsContent value="login" className="p-6 space-y-6">
                    <ImageUploader
                      label="Login Background Image"
                      field="login_background_url"
                      assetType="login_background"
                      inputRef={loginBgInputRef}
                      description="Full-page background for login screen. Recommended: 1920x1080px"
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label>Login Title</Label>
                        <Input
                          value={branding.login_title || ''}
                          onChange={(e) => handleChange('login_title', e.target.value)}
                          placeholder={branding.hospital_name || 'Hospital Name'}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">Leave empty to use hospital name</p>
                      </div>
                      <div>
                        <Label>Login Subtitle</Label>
                        <Input
                          value={branding.login_subtitle || ''}
                          onChange={(e) => handleChange('login_subtitle', e.target.value)}
                          placeholder="Staff Portal"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base">Show "Powered by Zesta Healthcare"</Label>
                        <p className="text-sm text-slate-500">Display attribution on login page</p>
                      </div>
                      <Switch
                        checked={branding.show_powered_by}
                        onCheckedChange={(checked) => handleChange('show_powered_by', checked)}
                      />
                    </div>
                  </TabsContent>

                  {/* Documents Tab */}
                  <TabsContent value="documents" className="p-6 space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base">Enable Letterhead</Label>
                        <p className="text-sm text-slate-500">Add hospital letterhead to all PDF documents</p>
                      </div>
                      <Switch
                        checked={branding.letterhead_enabled}
                        onCheckedChange={(checked) => handleChange('letterhead_enabled', checked)}
                      />
                    </div>

                    {branding.letterhead_enabled && (
                      <div>
                        <Label>Letterhead Address</Label>
                        <Textarea
                          value={branding.letterhead_address || ''}
                          onChange={(e) => handleChange('letterhead_address', e.target.value)}
                          placeholder="123 Medical Center Drive, City, State - 123456"
                          className="mt-1"
                          rows={2}
                        />
                        <p className="text-xs text-slate-500 mt-1">Address shown on document letterhead</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base">Enable Watermark</Label>
                        <p className="text-sm text-slate-500">Add watermark to PDF documents</p>
                      </div>
                      <Switch
                        checked={branding.watermark_enabled}
                        onCheckedChange={(checked) => handleChange('watermark_enabled', checked)}
                      />
                    </div>

                    {branding.watermark_enabled && (
                      <div>
                        <Label>Watermark Text</Label>
                        <Input
                          value={branding.watermark_text || ''}
                          onChange={(e) => handleChange('watermark_text', e.target.value)}
                          placeholder="CONFIDENTIAL"
                          className="mt-1"
                        />
                      </div>
                    )}
                  </TabsContent>

                  {/* Contact Tab */}
                  <TabsContent value="contact" className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label>Support Email</Label>
                        <Input
                          type="email"
                          value={branding.support_email || ''}
                          onChange={(e) => handleChange('support_email', e.target.value)}
                          placeholder="support@yourhospital.com"
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">Shown in emails and help sections</p>
                      </div>
                      <div>
                        <Label>Support Phone</Label>
                        <Input
                          value={branding.support_phone || ''}
                          onChange={(e) => handleChange('support_phone', e.target.value)}
                          placeholder="+91-9876543210"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Email Footer Text</Label>
                      <Textarea
                        value={branding.email_footer_text || ''}
                        onChange={(e) => handleChange('email_footer_text', e.target.value)}
                        placeholder="Thank you for choosing our hospital. For any queries, contact our support team."
                        className="mt-1"
                        rows={3}
                      />
                    </div>

                    <ColorPicker
                      label="Email Header Color"
                      field="email_header_color"
                      description="Background color for email headers"
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Live Preview
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                      onClick={() => setPreviewMode('desktop')}
                      className="h-8 w-8 p-0"
                    >
                      <Monitor className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                      onClick={() => setPreviewMode('mobile')}
                      className="h-8 w-8 p-0"
                    >
                      <Smartphone className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Login Preview */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Login Page</Label>
                  <div 
                    className={`rounded-lg overflow-hidden border shadow-sm ${previewMode === 'mobile' ? 'max-w-[200px] mx-auto' : ''}`}
                    style={{
                      background: branding.login_background_url 
                        ? `url(${branding.login_background_url}) center/cover`
                        : `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
                    }}
                  >
                    <div className="p-4 flex flex-col items-center bg-black/20 backdrop-blur-sm">
                      {branding.logo_url ? (
                        <img src={branding.logo_url} alt="Logo" className="h-10 mb-2 bg-white rounded p-1" />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold mb-2">
                          {branding.hospital_name?.charAt(0) || 'H'}
                        </div>
                      )}
                      <h3 className="text-white font-semibold text-sm">
                        {branding.login_title || branding.hospital_name || 'Hospital Name'}
                      </h3>
                      {branding.login_subtitle && (
                        <p className="text-white/80 text-xs">{branding.login_subtitle}</p>
                      )}
                      <div className="mt-3 w-full max-w-[150px] space-y-2">
                        <div className="h-6 bg-white/90 rounded text-xs flex items-center px-2 text-slate-400">Email</div>
                        <div className="h-6 bg-white/90 rounded text-xs flex items-center px-2 text-slate-400">Password</div>
                        <div 
                          className="h-6 rounded text-xs flex items-center justify-center text-white font-medium"
                          style={{ backgroundColor: branding.primary_color }}
                        >
                          Sign In
                        </div>
                      </div>
                      {branding.show_powered_by && (
                        <p className="text-white/50 text-[8px] mt-2">Powered by Zesta Healthcare</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sidebar Preview */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Sidebar</Label>
                  <div 
                    className="rounded-lg overflow-hidden w-[120px]"
                    style={{ backgroundColor: branding.sidebar_color }}
                  >
                    <div className="p-3">
                      {branding.logo_dark_url || branding.logo_url ? (
                        <img 
                          src={branding.logo_dark_url || branding.logo_url} 
                          alt="Logo" 
                          className="h-6 mb-3"
                        />
                      ) : (
                        <div className="h-6 w-6 rounded bg-white/20 mb-3" />
                      )}
                      <div className="space-y-1">
                        {['Dashboard', 'Patients', 'Billing'].map((item, i) => (
                          <div 
                            key={item}
                            className={`text-xs px-2 py-1 rounded ${i === 0 ? 'bg-white/20' : ''}`}
                            style={{ color: branding.sidebar_text_color }}
                          >
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Document Preview */}
                {branding.letterhead_enabled && (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Document Letterhead</Label>
                    <div className="rounded-lg border bg-white p-3 shadow-sm">
                      <div className="flex items-start gap-2 border-b pb-2 mb-2">
                        {branding.logo_url ? (
                          <img src={branding.logo_url} alt="Logo" className="h-8" />
                        ) : (
                          <div 
                            className="h-8 w-8 rounded flex items-center justify-center text-white font-bold text-xs"
                            style={{ backgroundColor: branding.primary_color }}
                          >
                            {branding.hospital_name?.charAt(0) || 'H'}
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold text-xs" style={{ color: branding.primary_color }}>
                            {branding.hospital_name || 'Hospital Name'}
                          </p>
                          {branding.tagline && (
                            <p className="text-[8px] text-slate-500">{branding.tagline}</p>
                          )}
                          {branding.letterhead_address && (
                            <p className="text-[8px] text-slate-400 mt-1">{branding.letterhead_address}</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="h-2 bg-slate-100 rounded w-full" />
                        <div className="h-2 bg-slate-100 rounded w-3/4" />
                        <div className="h-2 bg-slate-100 rounded w-5/6" />
                      </div>
                      {branding.watermark_enabled && branding.watermark_text && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-2xl font-bold text-slate-200 rotate-[-30deg] opacity-50">
                            {branding.watermark_text}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Color Palette */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Color Palette</Label>
                  <div className="flex gap-1">
                    <div 
                      className="h-8 flex-1 rounded-l-lg"
                      style={{ backgroundColor: branding.primary_color }}
                      title="Primary"
                    />
                    <div 
                      className="h-8 flex-1"
                      style={{ backgroundColor: branding.secondary_color }}
                      title="Secondary"
                    />
                    <div 
                      className="h-8 flex-1 rounded-r-lg"
                      style={{ backgroundColor: branding.accent_color }}
                      title="Accent"
                    />
                  </div>
                </div>

                {/* Full Preview Button */}
                <Button
                  onClick={() => setShowFullPreview(true)}
                  variant="outline"
                  className="w-full mt-4 flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Preview Full Login Page
                </Button>

                {/* PDF Preview Button */}
                <Button
                  onClick={() => setShowPdfPreview(true)}
                  variant="outline"
                  className="w-full mt-2 flex items-center gap-2"
                >
                  <FileDown className="h-4 w-4" />
                  Preview PDF Documents
                </Button>

                {/* Browse Templates Button */}
                <Button
                  onClick={() => setShowTemplateGallery(true)}
                  variant="outline"
                  className="w-full mt-2 flex items-center gap-2 border-dashed border-2 border-purple-300 text-purple-600 hover:bg-purple-50"
                >
                  <Sparkles className="h-4 w-4" />
                  Browse Templates
                </Button>

                {/* Email Templates Button */}
                <Button
                  onClick={openEmailTemplates}
                  variant="outline"
                  className="w-full mt-2 flex items-center gap-2 border-dashed border-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <Mail className="h-4 w-4" />
                  Email Templates
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>

      {/* Full Screen Login Preview Modal */}
      <AnimatePresence>
        {showFullPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowFullPreview(false)}
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-10 w-full max-w-5xl mx-4"
            >
              {/* Close button */}
              <Button
                onClick={() => setShowFullPreview(false)}
                variant="ghost"
                className="absolute -top-12 right-0 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5 mr-2" />
                Close Preview
              </Button>

              {/* Preview Info Banner */}
              <div className="bg-amber-500 text-amber-950 px-4 py-2 rounded-t-xl flex items-center gap-2 text-sm font-medium">
                <Eye className="h-4 w-4" />
                Preview Mode - This shows how your login page will look with the current (unsaved) branding settings
              </div>

              {/* Login Page Preview */}
              <div 
                className="rounded-b-xl overflow-hidden shadow-2xl"
                style={{
                  background: branding.login_background_url 
                    ? `url(${branding.login_background_url}) center/cover`
                    : `linear-gradient(135deg, #1e293b 0%, ${branding.primary_color} 50%, #1e293b 100%)`
                }}
              >
                {/* Overlay for background images */}
                {branding.login_background_url && (
                  <div className="absolute inset-0 bg-black/40 rounded-b-xl" />
                )}

                <div className="relative min-h-[500px] flex items-center justify-center p-8">
                  <div className="max-w-md w-full">
                    {/* Hospital Branding Header */}
                    <div className="text-center mb-6">
                      {branding.logo_url ? (
                        <img 
                          src={branding.logo_url} 
                          alt={branding.hospital_name || 'Hospital'} 
                          className="h-20 max-w-[200px] mx-auto mb-4 rounded-xl bg-white p-2 object-contain"
                        />
                      ) : (
                        <div 
                          className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
                          style={{ backgroundColor: branding.primary_color }}
                        >
                          <Building2 className="h-8 w-8 text-white" />
                        </div>
                      )}
                      <h1 className="text-2xl font-bold text-white">
                        {branding.login_title || branding.hospital_name || 'Hospital Name'}
                      </h1>
                      {(branding.login_subtitle || branding.tagline) && (
                        <p className="text-white/70 text-sm mt-1">
                          {branding.login_subtitle || branding.tagline}
                        </p>
                      )}
                    </div>

                    {/* Login Card */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl p-6">
                      <h2 className="text-white text-center text-lg font-semibold mb-4">Staff Login</h2>
                      
                      <div className="space-y-4">
                        {/* Email Field */}
                        <div className="space-y-2">
                          <label className="text-white/80 text-sm">Email Address</label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <input
                              type="email"
                              className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 pl-10 pr-4 py-2 rounded-lg focus:border-white/40 focus:outline-none"
                              placeholder="your.email@hospital.com"
                              readOnly
                            />
                          </div>
                        </div>

                        {/* Password Field */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-white/80 text-sm">Password</label>
                            <span className="text-xs text-white/60 hover:text-white cursor-pointer">
                              Forgot password?
                            </span>
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <input
                              type="password"
                              className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 pl-10 pr-10 py-2 rounded-lg focus:border-white/40 focus:outline-none"
                              placeholder="••••••••"
                              readOnly
                            />
                            <EyeOff className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
                          </div>
                        </div>

                        {/* Sign In Button */}
                        <button
                          className="w-full py-3 text-white font-medium rounded-lg transition-all hover:opacity-90"
                          style={{ 
                            background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)` 
                          }}
                        >
                          Sign In
                        </button>
                      </div>

                      <div className="mt-6 pt-6 border-t border-white/10 text-center">
                        <p className="text-white/40 text-xs">
                          This is a secure login for {branding.hospital_name || 'hospital'} staff only.
                        </p>
                        <span className="text-xs text-white/60 hover:text-white mt-2 inline-block cursor-pointer">
                          Not your hospital? Use main login
                        </span>
                      </div>
                    </div>

                    {/* Powered By */}
                    {branding.show_powered_by && (
                      <p className="text-center text-white/30 text-xs mt-6">
                        Powered by Zesta Healthcare Platform
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview Actions */}
              <div className="mt-4 flex justify-center gap-3">
                <Button
                  onClick={() => setShowFullPreview(false)}
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Close Preview
                </Button>
                {hasChanges && (
                  <Button
                    onClick={() => {
                      handleSave();
                      setShowFullPreview(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save & Apply Changes
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PDF Document Preview Modal */}
      <AnimatePresence>
        {showPdfPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4"
          >
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowPdfPreview(false)}
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-10 w-full max-w-4xl mx-4"
            >
              {/* Close button */}
              <Button
                onClick={() => setShowPdfPreview(false)}
                variant="ghost"
                className="absolute -top-12 right-0 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5 mr-2" />
                Close Preview
              </Button>

              {/* Preview Info Banner */}
              <div className="bg-blue-500 text-white px-4 py-2 rounded-t-xl flex items-center gap-2 text-sm font-medium">
                <FileDown className="h-4 w-4" />
                PDF Preview Mode - See how your branded documents will look
              </div>

              {/* PDF Type Tabs */}
              <div className="bg-slate-800 px-4 py-3 flex gap-2">
                {[
                  { id: 'prescription', label: 'Prescription', icon: Pill },
                  { id: 'discharge', label: 'Discharge Summary', icon: ClipboardList },
                  { id: 'lab_report', label: 'Lab Report', icon: TestTube }
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setPdfPreviewType(id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                      pdfPreviewType === id 
                        ? 'bg-white text-slate-800' 
                        : 'text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>

              {/* PDF Preview Container */}
              <div className="bg-slate-100 p-6 rounded-b-xl max-h-[70vh] overflow-y-auto">
                {/* A4 Paper Preview */}
                <div className="bg-white shadow-xl mx-auto" style={{ width: '595px', minHeight: '842px', padding: '40px' }}>
                  
                  {/* Letterhead */}
                  <div className="border-b-2 pb-4 mb-6" style={{ borderColor: branding.primary_color }}>
                    <div className="flex items-start justify-between">
                      {/* Logo and Hospital Info */}
                      <div className="flex items-center gap-4">
                        {branding.logo_url ? (
                          <img 
                            src={branding.logo_url} 
                            alt="Hospital Logo" 
                            className="h-16 w-auto object-contain"
                          />
                        ) : (
                          <div 
                            className="w-16 h-16 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: branding.primary_color }}
                          >
                            <Building2 className="h-8 w-8 text-white" />
                          </div>
                        )}
                        <div>
                          <h1 className="text-xl font-bold" style={{ color: branding.primary_color }}>
                            {branding.hospital_name || 'Hospital Name'}
                          </h1>
                          {branding.tagline && (
                            <p className="text-sm text-slate-500">{branding.tagline}</p>
                          )}
                          {branding.letterhead_address && (
                            <p className="text-xs text-slate-400 mt-1 max-w-[300px]">
                              {branding.letterhead_address}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Contact Info */}
                      <div className="text-right text-xs text-slate-500">
                        {branding.support_phone && (
                          <p className="flex items-center justify-end gap-1">
                            <Phone className="h-3 w-3" /> {branding.support_phone}
                          </p>
                        )}
                        {branding.support_email && (
                          <p className="flex items-center justify-end gap-1 mt-1">
                            <Mail className="h-3 w-3" /> {branding.support_email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Document Title */}
                  <div className="text-center mb-6">
                    <h2 className="text-lg font-bold uppercase tracking-wide" style={{ color: branding.primary_color }}>
                      {pdfPreviewType === 'prescription' && 'Medical Prescription'}
                      {pdfPreviewType === 'discharge' && 'Discharge Summary'}
                      {pdfPreviewType === 'lab_report' && 'Laboratory Report'}
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">Document Date: {new Date().toLocaleDateString()}</p>
                  </div>

                  {/* Patient Info Section */}
                  <div className="mb-6 p-3 rounded-lg bg-slate-50">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: branding.primary_color }}>
                      <User className="h-4 w-4" /> Patient Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-500">Name:</span> <span className="font-medium">John Doe</span></div>
                      <div><span className="text-slate-500">Patient ID:</span> <span className="font-medium">PAT-2024-001</span></div>
                      <div><span className="text-slate-500">Age/Gender:</span> <span className="font-medium">45 / Male</span></div>
                      <div><span className="text-slate-500">Date:</span> <span className="font-medium">{new Date().toLocaleDateString()}</span></div>
                    </div>
                  </div>

                  {/* Content based on document type */}
                  {pdfPreviewType === 'prescription' && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: branding.primary_color }}>
                        <Pill className="h-4 w-4" /> Medications
                      </h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ backgroundColor: branding.primary_color }}>
                            <th className="text-left p-2 text-white font-medium">#</th>
                            <th className="text-left p-2 text-white font-medium">Medication</th>
                            <th className="text-left p-2 text-white font-medium">Dosage</th>
                            <th className="text-left p-2 text-white font-medium">Frequency</th>
                            <th className="text-left p-2 text-white font-medium">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-slate-200">
                            <td className="p-2">1</td>
                            <td className="p-2 font-medium">Amoxicillin 500mg</td>
                            <td className="p-2">500mg</td>
                            <td className="p-2">Twice daily</td>
                            <td className="p-2">7 days</td>
                          </tr>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <td className="p-2">2</td>
                            <td className="p-2 font-medium">Paracetamol 650mg</td>
                            <td className="p-2">650mg</td>
                            <td className="p-2">As needed</td>
                            <td className="p-2">5 days</td>
                          </tr>
                          <tr className="border-b border-slate-200">
                            <td className="p-2">3</td>
                            <td className="p-2 font-medium">Vitamin D3</td>
                            <td className="p-2">60000 IU</td>
                            <td className="p-2">Weekly</td>
                            <td className="p-2">8 weeks</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                        <p className="text-xs text-amber-800">
                          <strong>Instructions:</strong> Take medications with food. Complete the full course of antibiotics.
                        </p>
                      </div>
                    </div>
                  )}

                  {pdfPreviewType === 'discharge' && (
                    <div className="mb-6 space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: branding.primary_color }}>
                          <Calendar className="h-4 w-4" /> Admission Details
                        </h3>
                        <div className="grid grid-cols-2 gap-2 text-sm bg-slate-50 p-3 rounded-lg">
                          <div><span className="text-slate-500">Admission Date:</span> <span className="font-medium">Jan 15, 2026</span></div>
                          <div><span className="text-slate-500">Discharge Date:</span> <span className="font-medium">Jan 20, 2026</span></div>
                          <div><span className="text-slate-500">Ward:</span> <span className="font-medium">General Ward - Bed 12</span></div>
                          <div><span className="text-slate-500">Attending Doctor:</span> <span className="font-medium">Dr. Smith</span></div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold mb-2" style={{ color: branding.primary_color }}>Diagnosis</h3>
                        <p className="text-sm bg-slate-50 p-3 rounded-lg">Acute Upper Respiratory Infection with mild pneumonia</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold mb-2" style={{ color: branding.primary_color }}>Treatment Summary</h3>
                        <p className="text-sm bg-slate-50 p-3 rounded-lg">Patient was treated with IV antibiotics and supportive care. Responded well to treatment.</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold mb-2" style={{ color: branding.primary_color }}>Follow-up Instructions</h3>
                        <ul className="text-sm bg-slate-50 p-3 rounded-lg list-disc list-inside space-y-1">
                          <li>Follow up in OPD after 1 week</li>
                          <li>Continue prescribed medications</li>
                          <li>Rest and adequate hydration</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {pdfPreviewType === 'lab_report' && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: branding.primary_color }}>
                        <TestTube className="h-4 w-4" /> Test Results - Complete Blood Count (CBC)
                      </h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ backgroundColor: branding.primary_color }}>
                            <th className="text-left p-2 text-white font-medium">Test</th>
                            <th className="text-left p-2 text-white font-medium">Result</th>
                            <th className="text-left p-2 text-white font-medium">Unit</th>
                            <th className="text-left p-2 text-white font-medium">Reference Range</th>
                            <th className="text-left p-2 text-white font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-slate-200">
                            <td className="p-2">Hemoglobin</td>
                            <td className="p-2 font-medium">14.2</td>
                            <td className="p-2">g/dL</td>
                            <td className="p-2">12.0 - 16.0</td>
                            <td className="p-2"><span className="text-green-600 font-medium">Normal</span></td>
                          </tr>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <td className="p-2">WBC Count</td>
                            <td className="p-2 font-medium">11.5</td>
                            <td className="p-2">×10³/µL</td>
                            <td className="p-2">4.0 - 11.0</td>
                            <td className="p-2"><span className="text-amber-600 font-medium">High</span></td>
                          </tr>
                          <tr className="border-b border-slate-200">
                            <td className="p-2">Platelet Count</td>
                            <td className="p-2 font-medium">245</td>
                            <td className="p-2">×10³/µL</td>
                            <td className="p-2">150 - 400</td>
                            <td className="p-2"><span className="text-green-600 font-medium">Normal</span></td>
                          </tr>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <td className="p-2">RBC Count</td>
                            <td className="p-2 font-medium">4.8</td>
                            <td className="p-2">×10⁶/µL</td>
                            <td className="p-2">4.5 - 5.5</td>
                            <td className="p-2"><span className="text-green-600 font-medium">Normal</span></td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-800">
                          <strong>Interpretation:</strong> Slightly elevated WBC count may indicate mild infection. All other parameters within normal limits.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Watermark Preview */}
                  {branding.watermark_enabled && branding.watermark_text && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      style={{ opacity: 0.05 }}
                    >
                      <p 
                        className="text-6xl font-bold transform -rotate-45"
                        style={{ color: branding.primary_color }}
                      >
                        {branding.watermark_text}
                      </p>
                    </div>
                  )}

                  {/* Doctor Signature Area */}
                  <div className="mt-8 pt-4 border-t border-slate-200">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-xs text-slate-400">Generated electronically</p>
                        <p className="text-xs text-slate-400">{new Date().toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <div className="h-12 w-32 border-b border-slate-300 mb-1"></div>
                        <p className="text-sm font-medium">Dr. John Smith</p>
                        <p className="text-xs text-slate-500">MBBS, MD</p>
                        <p className="text-xs text-slate-500">Reg. No: MH-12345</p>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="mt-6 pt-4 border-t text-center" style={{ borderColor: branding.primary_color }}>
                    <p className="text-xs text-slate-500">
                      {branding.footer_text || `© ${new Date().getFullYear()} ${branding.hospital_name || 'Hospital'}. All rights reserved.`}
                    </p>
                    {branding.support_email && (
                      <p className="text-xs text-slate-400 mt-1">
                        For queries: {branding.support_email} | {branding.support_phone || ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview Actions */}
              <div className="mt-4 flex justify-center gap-3">
                <Button
                  onClick={() => setShowPdfPreview(false)}
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Close Preview
                </Button>
                {hasChanges && (
                  <Button
                    onClick={() => {
                      handleSave();
                      setShowPdfPreview(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save & Apply Changes
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Gallery Modal */}
      <AnimatePresence>
        {showTemplateGallery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4"
          >
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowTemplateGallery(false)}
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-10 w-full max-w-5xl mx-4"
            >
              {/* Close button */}
              <Button
                onClick={() => setShowTemplateGallery(false)}
                variant="ghost"
                className="absolute -top-12 right-0 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5 mr-2" />
                Close Gallery
              </Button>

              {/* Gallery Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Branding Template Gallery</h2>
                    <p className="text-white/80 text-sm">Choose a pre-designed color scheme for your hospital</p>
                  </div>
                </div>
              </div>

              {/* Templates Grid */}
              <div className="bg-white p-6 rounded-b-xl max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {BRANDING_TEMPLATES.map((template) => {
                    const IconComponent = template.icon;
                    const isSelected = selectedTemplate === template.id;
                    
                    return (
                      <motion.div
                        key={template.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => applyTemplate(template)}
                        className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                          isSelected 
                            ? 'border-purple-500 ring-2 ring-purple-200' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {/* Template Preview */}
                        <div 
                          className="h-24 flex items-center justify-center relative"
                          style={{ background: template.preview }}
                        >
                          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                            <IconComponent className="h-8 w-8 text-white" />
                          </div>
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                              <Check className="h-4 w-4 text-purple-600" />
                            </div>
                          )}
                        </div>
                        
                        {/* Template Info */}
                        <div className="p-3 bg-white">
                          <h3 className="font-semibold text-sm text-slate-800">{template.name}</h3>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</p>
                          
                          {/* Color Swatches */}
                          <div className="flex gap-1 mt-2">
                            <div 
                              className="h-4 w-4 rounded-full border border-slate-200"
                              style={{ backgroundColor: template.colors.primary_color }}
                              title="Primary"
                            />
                            <div 
                              className="h-4 w-4 rounded-full border border-slate-200"
                              style={{ backgroundColor: template.colors.secondary_color }}
                              title="Secondary"
                            />
                            <div 
                              className="h-4 w-4 rounded-full border border-slate-200"
                              style={{ backgroundColor: template.colors.accent_color }}
                              title="Accent"
                            />
                            <div 
                              className="h-4 w-4 rounded-full border border-slate-200"
                              style={{ backgroundColor: template.colors.sidebar_color }}
                              title="Sidebar"
                            />
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Tips Section */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                    <Paintbrush className="h-4 w-4" />
                    Customization Tips
                  </h4>
                  <ul className="mt-2 text-xs text-slate-600 space-y-1">
                    <li>• Click on any template to apply its color scheme instantly</li>
                    <li>• Templates only change colors - your logos and images remain unchanged</li>
                    <li>• You can further customize colors after applying a template</li>
                    <li>• Remember to click "Save Changes" to keep your new look</li>
                  </ul>
                </div>

                {/* Custom Templates Section */}
                {customTemplates.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <FileCheck className="h-5 w-5" />
                      Your Custom Templates
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {customTemplates.map((template) => {
                        const IconComponent = ICON_MAP[template.icon] || Palette;
                        const isSelected = selectedTemplate === template.id;
                        
                        return (
                          <motion.div
                            key={template.id}
                            whileHover={{ scale: 1.02 }}
                            className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all relative ${
                              isSelected 
                                ? 'border-green-500 ring-2 ring-green-200' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            {/* Delete Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCustomTemplate(template.id);
                              }}
                              className="absolute top-2 right-2 z-10 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                            
                            <div 
                              onClick={() => applyTemplate(template)}
                              className="h-24 flex items-center justify-center"
                              style={{ 
                                background: `linear-gradient(135deg, ${template.colors.primary_color} 0%, ${template.colors.secondary_color} 100%)` 
                              }}
                            >
                              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                                <IconComponent className="h-8 w-8 text-white" />
                              </div>
                            </div>
                            
                            <div className="p-3 bg-white" onClick={() => applyTemplate(template)}>
                              <h3 className="font-semibold text-sm text-slate-800">{template.name}</h3>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description || 'Custom template'}</p>
                              <div className="flex gap-1 mt-2">
                                {Object.values(template.colors).slice(0, 4).map((color, i) => (
                                  <div 
                                    key={i}
                                    className="h-4 w-4 rounded-full border border-slate-200"
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Gallery Actions */}
              <div className="mt-4 flex justify-center gap-3">
                <Button
                  onClick={() => setShowTemplateGallery(false)}
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Close Gallery
                </Button>
                <Button
                  onClick={() => {
                    setShowTemplateGallery(false);
                    setShowSaveTemplateModal(true);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Save Current as Template
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Template Modal */}
      <AnimatePresence>
        {showSaveTemplateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowSaveTemplateModal(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-10 bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6"
            >
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Save className="h-5 w-5 text-purple-600" />
                Save as Custom Template
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Template Name *</Label>
                  <Input
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="My Hospital Theme"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <Textarea
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    placeholder="A brief description of this theme..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Icon</Label>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {Object.entries(ICON_MAP).map(([name, IconComp]) => (
                      <button
                        key={name}
                        onClick={() => setNewTemplateIcon(name)}
                        className={`p-2 rounded-lg border-2 transition-colors ${
                          newTemplateIcon === name 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <IconComp className="h-5 w-5" />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Preview Colors */}
                <div>
                  <Label className="text-sm font-medium">Colors to Save</Label>
                  <div className="flex gap-2 mt-2">
                    <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: branding.primary_color }} title="Primary" />
                    <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: branding.secondary_color }} title="Secondary" />
                    <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: branding.accent_color }} title="Accent" />
                    <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: branding.sidebar_color }} title="Sidebar" />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  onClick={() => setShowSaveTemplateModal(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveAsTemplate}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Template
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Email Templates Modal */}
      <AnimatePresence>
        {showEmailTemplates && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4"
          >
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowEmailTemplates(false)}
            />
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-10 w-full max-w-6xl mx-4"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Email Templates</h2>
                    <p className="text-white/80 text-sm">Customize emails with your hospital branding</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowEmailTemplates(false)}
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="bg-white rounded-b-xl">
                <div className="flex h-[70vh]">
                  {/* Template List Sidebar */}
                  <div className="w-64 border-r bg-slate-50 p-4 overflow-y-auto">
                    <h3 className="text-sm font-semibold text-slate-600 mb-3">Templates</h3>
                    <div className="space-y-2">
                      {Object.entries(emailTemplates).map(([key, template]) => {
                        const icons = {
                          appointment_confirmation: Calendar,
                          appointment_reminder: Bell,
                          password_reset: FileKey,
                          welcome_email: User,
                          lab_results_ready: TestTube,
                          prescription_ready: Pill,
                          invoice_receipt: CreditCard
                        };
                        const IconComp = icons[key] || Mail;
                        
                        return (
                          <button
                            key={key}
                            onClick={() => {
                              setSelectedEmailTemplate(key);
                              setEditingEmailTemplate(null);
                              setEmailPreview(null);
                            }}
                            className={`w-full text-left p-3 rounded-lg flex items-center gap-2 transition-colors ${
                              selectedEmailTemplate === key 
                                ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                : 'hover:bg-slate-100 text-slate-700'
                            }`}
                          >
                            <IconComp className="h-4 w-4" />
                            <span className="text-sm font-medium truncate">{template.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Template Editor */}
                  <div className="flex-1 flex">
                    {/* Editor Panel */}
                    <div className="flex-1 p-6 overflow-y-auto">
                      {emailTemplates[selectedEmailTemplate] && (
                        <>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-slate-800">
                              {emailTemplates[selectedEmailTemplate].name}
                            </h3>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => resetEmailTemplate(selectedEmailTemplate)}
                                variant="outline"
                                size="sm"
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Reset
                              </Button>
                              {!editingEmailTemplate ? (
                                <Button
                                  onClick={() => setEditingEmailTemplate({
                                    subject: emailTemplates[selectedEmailTemplate].subject,
                                    body: emailTemplates[selectedEmailTemplate].body
                                  })}
                                  size="sm"
                                >
                                  <Edit3 className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                              ) : (
                                <Button
                                  onClick={updateEmailTemplate}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <Label className="text-sm font-medium">Subject Line</Label>
                              {editingEmailTemplate ? (
                                <Input
                                  value={editingEmailTemplate.subject}
                                  onChange={(e) => setEditingEmailTemplate(prev => ({
                                    ...prev,
                                    subject: e.target.value
                                  }))}
                                  className="mt-1 font-mono text-sm"
                                />
                              ) : (
                                <div className="mt-1 p-3 bg-slate-50 rounded-lg text-sm font-mono">
                                  {emailTemplates[selectedEmailTemplate].subject}
                                </div>
                              )}
                            </div>

                            <div>
                              <Label className="text-sm font-medium">Email Body (HTML)</Label>
                              {editingEmailTemplate ? (
                                <Textarea
                                  value={editingEmailTemplate.body}
                                  onChange={(e) => setEditingEmailTemplate(prev => ({
                                    ...prev,
                                    body: e.target.value
                                  }))}
                                  className="mt-1 font-mono text-sm"
                                  rows={15}
                                />
                              ) : (
                                <div className="mt-1 p-3 bg-slate-50 rounded-lg text-sm font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                                  {emailTemplates[selectedEmailTemplate].body}
                                </div>
                              )}
                            </div>

                            {/* Variables Help */}
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <h4 className="text-sm font-semibold text-blue-800 mb-2">Available Variables</h4>
                              <div className="flex flex-wrap gap-2">
                                {['patient_name', 'hospital_name', 'doctor_name', 'appointment_date', 'appointment_time', 'primary_color', 'support_email', 'support_phone'].map(v => (
                                  <code key={v} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    {`{{${v}}}`}
                                  </code>
                                ))}
                              </div>
                            </div>

                            <Button
                              onClick={() => previewEmailTemplate(editingEmailTemplate || emailTemplates[selectedEmailTemplate])}
                              variant="outline"
                              className="w-full"
                              disabled={loadingEmailPreview}
                            >
                              {loadingEmailPreview ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4 mr-2" />
                              )}
                              Generate Preview
                            </Button>

                            {/* Send Test Email Section */}
                            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                              <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                                <Send className="h-4 w-4" />
                                Send Test Email
                              </h4>
                              <p className="text-xs text-green-700 mb-3">
                                Send this template to your email to see how it looks in real email clients.
                              </p>
                              <div className="flex gap-2">
                                <Input
                                  type="email"
                                  placeholder="your@email.com"
                                  value={testEmailRecipient}
                                  onChange={(e) => setTestEmailRecipient(e.target.value)}
                                  className="flex-1 text-sm"
                                />
                                <Button
                                  onClick={() => sendTestEmail(
                                    editingEmailTemplate || emailTemplates[selectedEmailTemplate],
                                    testEmailRecipient
                                  )}
                                  disabled={sendingTestEmail || !testEmailRecipient}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  size="sm"
                                >
                                  {sendingTestEmail ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Send className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Preview Panel */}
                    <div className="w-[400px] border-l bg-slate-50 p-4 overflow-y-auto">
                      <h3 className="text-sm font-semibold text-slate-600 mb-3">Email Preview</h3>
                      
                      {emailPreview ? (
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                          {/* Email Header */}
                          <div 
                            className="p-4 text-center"
                            style={{ backgroundColor: emailBranding.email_header_color || emailBranding.primary_color }}
                          >
                            {emailBranding.logo_url ? (
                              <img 
                                src={emailBranding.logo_url} 
                                alt="Logo" 
                                className="h-10 mx-auto"
                              />
                            ) : (
                              <div className="text-white font-bold text-lg">
                                {emailBranding.hospital_name || 'Hospital'}
                              </div>
                            )}
                          </div>
                          
                          {/* Email Subject */}
                          <div className="border-b p-3 bg-slate-100">
                            <p className="text-xs text-slate-500">Subject:</p>
                            <p className="font-medium text-slate-800">{emailPreview.subject}</p>
                          </div>
                          
                          {/* Email Body */}
                          <div 
                            className="p-4 text-sm"
                            dangerouslySetInnerHTML={{ __html: emailPreview.body }}
                          />
                          
                          {/* Email Footer */}
                          <div className="border-t p-4 bg-slate-50 text-center text-xs text-slate-500">
                            {emailBranding.footer_text || `© ${new Date().getFullYear()} ${emailBranding.hospital_name}`}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-400">
                          <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p className="text-sm">Click "Generate Preview" to see how your email will look</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
